/**
 * API service for sending a schedule to the tablet now (BA-84 endpoints, BA-85 dashboard)
 * contracts/firebase-schema.md §3.2
 */

import { auth } from '../firebase';
import { SentSchedule, SendScheduleRequest } from '../types/sentSchedule';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** A send or a Remove stops waiting for the backend after this long */
export const SENT_SCHEDULE_TIMEOUT_MS = 15000;

/**
 * An error with the HTTP status, so a caller can pick its message.
 * status 0: no answer (network error or the 15 s timeout); the write may still have landed.
 */
export class SentScheduleApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'SentScheduleApiError';
  }
}

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new SentScheduleApiError('Not authenticated', 401);
  }
  return await user.getIdToken();
}

async function call(deviceId: string, init: RequestInit): Promise<SentSchedule> {
  const token = await getAuthToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SENT_SCHEDULE_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/devices/${deviceId}/sent-schedule`, {
      ...init,
      headers: { ...(init.headers || {}), 'Authorization': `Bearer ${token}` },
      signal: controller.signal,
    });
  } catch {
    throw new SentScheduleApiError('No answer', 0);
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: '' }));
    throw new SentScheduleApiError(error.detail || `HTTP ${response.status}`, response.status);
  }
  return response.json();
}

export const sentScheduleService = {
  /** POST: the new node, active. 400 not sendable, 403 viewer, 404 schedule deleted */
  send(deviceId: string, scheduleId: string): Promise<SentSchedule> {
    const body: SendScheduleRequest = { scheduleId };
    return call(deviceId, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  /** DELETE: the ended node. 404 there is none, 409 it had already ended */
  remove(deviceId: string): Promise<SentSchedule> {
    return call(deviceId, { method: 'DELETE' });
  },
};
